﻿interface CommentExample {
    url: string;
    alt?: string;
}

class BlurredContentReveal extends HTMLElement {
    private revealed: boolean[] = [];
    private commentExamples: CommentExample[] = [
        {
            url: "./TrollWallTheme/images/assets/comment-politics-1.png",
        },
        {
            url: "./TrollWallTheme/images/assets/comment-politics-2.png",
        },
        {
            url: "./TrollWallTheme/images/assets/comment-politics-3.png",
        },
    ];

    static get observedAttributes() {
        return ["data-examples"];
    }

    constructor() {
        super();
        // revealed will be initialized after examples are resolved
    }

    connectedCallback() {
        this.loadExamplesFromMarkup();
        this.render();
    }

    attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null) {
        if (name === "data-examples" && newValue !== _oldValue) {
            this.loadExamplesFromMarkup();
            this.render();
        }
    }

    private loadExamplesFromMarkup() {
        // 1) Try data-examples attribute (stringified JSON)
        const attr = this.getAttribute("data-examples");
        if (attr) {
            try {
                const parsed = JSON.parse(attr) as CommentExample[];
                if (Array.isArray(parsed) && parsed.length > 0) {
                    this.commentExamples = parsed;
                    this.revealed = this.commentExamples.map(() => false);
                    return;
                }
            } catch (e) {
                // fallthrough to script tag fallback
                console.warn("blurred-content-reveal: failed to parse data-examples JSON", e);
            }
        }

        // 2) Fallback: <script type="application/json"> inside the component
        const script = this.querySelector('script[type="application/json"]');
        if (script) {
            try {
                const parsed = JSON.parse(script.textContent || "") as CommentExample[];
                if (Array.isArray(parsed) && parsed.length > 0) {
                    this.commentExamples = parsed;
                    this.revealed = this.commentExamples.map(() => false);
                    return;
                }
            } catch (e) {
                console.warn("blurred-content-reveal: failed to parse JSON from script[type=application/json]", e);
            }
        }

        // 3) If neither provided or parsing failed, ensure revealed length matches defaults
        this.revealed = this.commentExamples.map(() => false);
    }

    private toggleReveal(index: number) {
        this.revealed[index] = !this.revealed[index];
        this.render();
    }

    private toggleAll() {
        const allRevealed = this.revealed.every((r) => r);
        this.revealed = this.revealed.map(() => !allRevealed);
        this.render();
    }

    private render() {
        this.innerHTML = `
          <button id="toggleAllBtn" aria-pressed="${this.revealed.every((r) => r)}"
            class="mt-4 mb-12 bg-transparent border px-3 py-1 rounded text-sm flex items-center justify-center mx-auto">
            ${this.revealed.every((r) => r)
                ? `
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round" 
                       class="lucide lucide-eye-off w-4 h-4 mr-2">
                    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 
                             10.747 10.747 0 0 1-1.444 2.49"></path>
                    <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path>
                    <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 
                             1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path>
                    <path d="m2 2 20 20"></path>
                  </svg>
                  Blur All
                `
                : `
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round" 
                       class="lucide lucide-eye w-4 h-4 mr-2">
                    <path d="M2.062 12.348a1 1 0 0 1 0-.696 
                             10.75 10.75 0 0 1 19.876 0 
                             1 1 0 0 1 0 .696 
                             10.75 10.75 0 0 1-19.876 0"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  Reveal All
                `
            }
          </button>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          ${this.commentExamples
                .map(
                    (example, index) => {
                        const isRevealed = this.revealed[index];
                        const alt = example.alt ? example.alt.replace(/"/g, "&quot;") : `Example ${index + 1}`;
                        return `
            <button data-index="${index}" aria-pressed="${isRevealed}"
              class="relative aspect-[4/3] rounded-lg overflow-hidden border-2 border-border hover:border-primary transition-all group cursor-pointer p-0">
              
              <div class="absolute inset-0 transition-all duration-300 ${isRevealed ? "blur-none" : "blur-2xl"}">
                <img src="${example.url}" alt="${alt}" loading="lazy"
                     class="w-full h-full object-cover block" />
                <div class="absolute inset-0 bg-gradient-to-br from-transparent to-black/10 pointer-events-none"></div>
              </div>

              <div class="absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity duration-300 ${isRevealed ? "opacity-0 pointer-events-none" : "opacity-100"}">
                <div class="text-white text-center px-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round" 
                       class="lucide lucide-eye w-8 h-8 mx-auto mb-2 group-hover:scale-110 transition-transform">
                    <path d="M2.062 12.348a1 1 0 0 1 0-.696 
                             10.75 10.75 0 0 1 19.876 0 
                             1 1 0 0 1 0 .696 
                             10.75 10.75 0 0 1-19.876 0"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <p class="text-sm font-medium">Click to reveal</p>
                </div>
              </div>
            </button>
          `;
                    }
                )
                .join("")}
  `;

        // Event listeners
        this.querySelector("#toggleAllBtn")?.addEventListener("click", () =>
            this.toggleAll()
        );
        this.querySelectorAll("button[data-index]").forEach((btn) => {
            const index = parseInt((btn as HTMLElement).dataset.index || "0", 10);
            btn.addEventListener("click", () => this.toggleReveal(index));
        });
    }
}

customElements.define("blurred-content-reveal", BlurredContentReveal);